import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, orderBy, Timestamp, limit } from 'firebase/firestore';
import { CalendarIcon, ClockIcon, VideoIcon, MapPinIcon, LoaderIcon, BookOpenIcon, CheckCircleIcon, ChevronRightIcon } from 'lucide-react';
import { selfLinkStudentToTeachers } from '../../services/studentService';
import { isLessonOnline } from '../../utils/lessonHelpers';
import { StudentJoinLessonPanel } from '../modals/StudentJoinLessonPanel';
interface UpcomingLesson {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherAvatar: string;
  instrument: string;
  date: string;
  time: string;
  duration: string;
  format: 'Virtual' | 'In-person';
  status: string;
  meetingLink?: string;
  notes?: string;
  taskTitle?: string;
  taskDueDate?: string;
  taskStatus?: string;
  taskNotes?: string;
  isNext: boolean;
  startAt: Date;
  durationMinutes: number;
  location: string;
  rawStatus: string;
}
// Cache keys and TTL
const CACHE_KEY = 'studentDashboard_lessons';
const CACHE_NAME_KEY = 'studentDashboard_name';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const SELF_LINK_KEY = 'studentDashboard_selfLinked';
function getCachedData<T>(key: string, uid: string): T | null {
  try {
    const raw = sessionStorage.getItem(`${key}_${uid}`);
    if (!raw) return null;
    const {
      data,
      timestamp
    } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_TTL) return data;
    return null;
  } catch {
    return null;
  }
}
function setCachedData<T>(key: string, uid: string, data: T): void {
  try {
    sessionStorage.setItem(`${key}_${uid}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [studentName, setStudentName] = useState('');
  const [upcomingLessons, setUpcomingLessons] = useState<UpcomingLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchInProgress = useRef(false);
  // State for Join Lesson Panel
  const [isJoinLessonPanelOpen, setIsJoinLessonPanelOpen] = useState(false);
  const [selectedLessonForJoin, setSelectedLessonForJoin] = useState<UpcomingLesson | null>(null);
  const formatLessons = useCallback((topLessons: any[], teacherNameMap: Record<string, string>, taskTitleMap: Record<string, {
    title: string;
    dueDate?: string;
    status?: string;
    notes?: string;
  }>): UpcomingLesson[] => {
    return topLessons.map((lesson, index) => {
      const startDate = lesson.startAt?.toDate ? lesson.startAt.toDate() : new Date(lesson.startAt);
      const teacherFullName = teacherNameMap[lesson.teacherId] || 'Teacher';
      const initials = teacherFullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);
      let dateStr: string;
      if (startDate.toDateString() === today.toDateString()) {
        dateStr = 'Today';
      } else if (startDate.toDateString() === tomorrow.toDateString()) {
        dateStr = 'Tomorrow';
      } else {
        dateStr = startDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });
      }
      const timeStr = startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const durationMin = lesson.durationMinutes || 60;
      const durationStr = `${durationMin} min`;
      const location = (lesson.location || '').toLowerCase();
      const isOnline = location.includes('online') || location.includes('virtual') || location.includes('zoom') || location.includes('meet') || !!lesson.meetingLink;
      const format: 'Virtual' | 'In-person' = isOnline ? 'Virtual' : 'In-person';
      const rawStatus = (lesson.status || 'SCHEDULED').toUpperCase();
      let statusDisplay = 'Scheduled';
      if (rawStatus === 'SCHEDULED') statusDisplay = 'Confirmed';else if (rawStatus === 'COMPLETED') statusDisplay = 'Completed';
      const linkedTask = taskTitleMap[lesson.id];
      return {
        id: lesson.id,
        teacherId: lesson.teacherId,
        teacherName: teacherFullName,
        teacherAvatar: initials,
        instrument: lesson.instrument || 'Music',
        date: dateStr,
        time: timeStr,
        duration: durationStr,
        format,
        status: statusDisplay,
        meetingLink: lesson.meetingLink,
        notes: lesson.notes || '',
        taskTitle: linkedTask?.title || '',
        taskDueDate: linkedTask?.dueDate || '',
        taskStatus: linkedTask?.status || '',
        taskNotes: linkedTask?.notes || '',
        isNext: index === 0,
        startAt: startDate,
        durationMinutes: durationMin,
        location: lesson.location || '',
        rawStatus
      };
    });
  }, []);
  useEffect(() => {
    if (!currentUser?.uid) return;
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    const uid = currentUser.uid;
    // Instantly show cached data if available
    const cachedLessons = getCachedData<UpcomingLesson[]>(CACHE_KEY, uid);
    const cachedName = getCachedData<string>(CACHE_NAME_KEY, uid);
    if (cachedLessons && cachedLessons.length > 0) {
      // Rehydrate Date objects lost during JSON serialization
      const rehydrated = cachedLessons.map(l => ({
        ...l,
        startAt: new Date(l.startAt)
      }));
      setUpcomingLessons(rehydrated);
      setIsLoading(false);
    }
    if (cachedName) {
      setStudentName(cachedName);
    }
    const fetchDashboardData = async () => {
      // Only show loading spinner if we have no cached data
      if (!cachedLessons || cachedLessons.length === 0) {
        setIsLoading(true);
      }
      try {
        // Step 1: Get student name + self-link (only once per session)
        const userDocRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const name = userData.firstName || userData.name || 'Student';
          setStudentName(name);
          setCachedData(CACHE_NAME_KEY, uid, name);
          // Self-link only once per session
          const alreadyLinked = sessionStorage.getItem(`${SELF_LINK_KEY}_${uid}`);
          if (!alreadyLinked) {
            const studentEmail = userData.email || currentUser.email;
            if (studentEmail) {
              try {
                await selfLinkStudentToTeachers(studentEmail, uid);
                sessionStorage.setItem(`${SELF_LINK_KEY}_${uid}`, 'true');
              } catch (e) {
                console.error('[StudentDashboard] selfLink failed:', e);
              }
            }
          }
        }
        // Step 2: Find teacher-student links
        const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', uid));
        const studentsSnap = await getDocs(studentsQuery);
        if (studentsSnap.empty) {
          setUpcomingLessons([]);
          setCachedData(CACHE_KEY, uid, []);
          setIsLoading(false);
          fetchInProgress.current = false;
          return;
        }
        // Build student-teacher pairs
        const studentTeacherPairs: {
          studentId: string;
          teacherId: string;
        }[] = [];
        studentsSnap.docs.forEach(docSnap => {
          const pathSegments = docSnap.ref.path.split('/');
          const teacherId = pathSegments[1];
          const studentData = docSnap.data();
          const studentIdForLessons = studentData.studentId || docSnap.id;
          studentTeacherPairs.push({
            studentId: studentIdForLessons,
            teacherId
          });
        });
        // Step 3: Query upcoming lessons (parallel per teacher)
        const now = new Date();
        const lessonPromises = studentTeacherPairs.map(pair => {
          const calendarQuery = query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), where('startAt', '>=', Timestamp.fromDate(now)), orderBy('startAt', 'asc'), limit(5));
          return getDocs(calendarQuery).then(snap => {
            const lessons: any[] = [];
            snap.docs.forEach(lessonDoc => {
              const data = lessonDoc.data();
              if (data.status !== 'CANCELED' && data.cancelled !== true) {
                lessons.push({
                  id: lessonDoc.id,
                  teacherId: pair.teacherId,
                  ...data
                });
              }
            });
            return lessons;
          });
        });
        const lessonResults = await Promise.all(lessonPromises);
        const allLessons = lessonResults.flat();
        allLessons.sort((a, b) => {
          const aTime = a.startAt?.toDate ? a.startAt.toDate().getTime() : new Date(a.startAt).getTime();
          const bTime = b.startAt?.toDate ? b.startAt.toDate().getTime() : new Date(b.startAt).getTime();
          return aTime - bTime;
        });
        const topLessons = allLessons.slice(0, 3);
        // Step 4: Resolve teacher names + tasks in parallel
        const teacherIds = [...new Set(topLessons.map(l => l.teacherId))];
        const teacherNameMap: Record<string, string> = {};
        const taskTitleMap: Record<string, {
          title: string;
          dueDate?: string;
          status?: string;
          notes?: string;
        }> = {};
        // Fetch teacher names and tasks in parallel
        await Promise.all(teacherIds.map(async tid => {
          // Teacher name
          const namePromise = getDoc(doc(db, 'publicTeacherProfiles', tid)).then(profileDoc => {
            if (profileDoc.exists()) {
              const p = profileDoc.data();
              teacherNameMap[tid] = p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Teacher';
            } else {
              teacherNameMap[tid] = 'Teacher';
            }
          }).catch(() => {
            teacherNameMap[tid] = 'Teacher';
          });
          // Tasks for this teacher
          const lessonIdsForTeacher = topLessons.filter(l => l.teacherId === tid).map(l => l.id);
          const taskPromise = lessonIdsForTeacher.length > 0 ? getDocs(collection(db, 'teachers', tid, 'tasks')).then(tasksSnap => {
            tasksSnap.forEach(taskDoc => {
              const taskData = taskDoc.data();
              if (taskData.lessonId && lessonIdsForTeacher.includes(taskData.lessonId) && taskData.title) {
                let formattedDueDate = '';
                if (taskData.dueDate) {
                  try {
                    const d = taskData.dueDate?.toDate ? taskData.dueDate.toDate() : new Date(taskData.dueDate);
                    if (!isNaN(d.getTime())) {
                      formattedDueDate = d.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      });
                    }
                  } catch {
                    formattedDueDate = '';
                  }
                }
                taskTitleMap[taskData.lessonId] = {
                  title: taskData.title,
                  dueDate: formattedDueDate,
                  status: taskData.status || '',
                  notes: taskData.notes || ''
                };
              }
            });
          }).catch(() => {}) : Promise.resolve();
          await Promise.all([namePromise, taskPromise]);
        }));
        // Step 5: Format and update
        const formatted = formatLessons(topLessons, teacherNameMap, taskTitleMap);
        setUpcomingLessons(formatted);
        setCachedData(CACHE_KEY, uid, formatted);
      } catch (error) {
        console.error('[StudentDashboard] Error fetching data:', error);
        if (!cachedLessons || cachedLessons.length === 0) {
          setUpcomingLessons([]);
        }
      } finally {
        setIsLoading(false);
        fetchInProgress.current = false;
      }
    };
    fetchDashboardData();
  }, [currentUser?.uid, formatLessons]);
  return <div className={`min-h-screen ${isDark ? '' : 'bg-gray-100/60'} p-6 md:p-8`} data-id="element-7122">
      <div className="max-w-4xl space-y-8" data-id="element-7123">
        {/* Welcome Header */}
        <div className={`${isDark ? '' : 'pb-6 border-b border-gray-200/80'}`} data-id="element-7124">
          <h1 className={`text-3xl md:text-4xl font-extrabold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7125">
            Welcome back{studentName ? `, ${studentName}` : ''}
          </h1>
          <p className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7126">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>

        {/* Upcoming Lessons Section */}
        <div data-id="element-7127">
          <div className="flex items-center gap-2.5 mb-5" data-id="element-7128">
            <div className={`p-1.5 rounded-lg ${isDark ? 'bg-navy-800' : 'bg-blue-50'}`} data-id="element-7129">
              <CalendarIcon className={`h-4.5 w-4.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} data-id="element-7130" />
            </div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7131">
              Next Lesson
            </h2>
          </div>

          {isLoading ? <div className={`rounded-xl p-12 text-center ${isDark ? 'bg-navy-800 border border-navy-700' : 'bg-white shadow-sm shadow-gray-200/50 border border-gray-100'}`} data-id="element-7132">
              <LoaderIcon className={`w-8 h-8 animate-spin mx-auto mb-3 ${isDark ? 'text-green-400' : 'text-green-500'}`} data-id="element-7133" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7134">
                Loading your lessons...
              </p>
            </div> : upcomingLessons.length > 0 ? <div className="space-y-4" data-id="element-7135">
              {/* Hero card — first upcoming lesson only */}
              {(() => {
            const lesson = upcomingLessons[0];
            return <div key={lesson.id} className={`rounded-xl p-5 cursor-pointer transition-all duration-200 ease-out ${isDark ? 'bg-navy-800 border border-blue-500/20 shadow-md shadow-blue-500/5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/15 hover:bg-navy-800/80' : 'bg-white border border-blue-200/60 shadow-md shadow-blue-100/40 ring-1 ring-blue-50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-100/60'}`} data-id="element-7136">
                    <div className="flex items-center justify-between gap-4" data-id="element-7137">
                      <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-7138">
                        {/* Teacher Avatar */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-base font-semibold flex-shrink-0 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`} data-id="element-7139">
                          {lesson.teacherAvatar}
                        </div>

                        {/* Lesson info */}
                        <div className="flex-1 min-w-0" data-id="element-7140">
                          <div className="flex items-center gap-2 mb-1" data-id="element-7141">
                            <h3 className={`text-base font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7142">
                              {lesson.instrument}
                            </h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700 ring-1 ring-green-200/50'}`} data-id="element-7143">
                              {lesson.status}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-50 text-gray-500 ring-1 ring-gray-200/50'}`} data-id="element-7144">
                              {lesson.duration}
                            </span>
                          </div>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mb-1`} data-id="element-7145">
                            with {lesson.teacherName}
                          </p>
                          <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-7146">
                            <div className="flex items-center gap-1.5" data-id="element-7147">
                              <ClockIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7148" />
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-7149">
                                {lesson.date} at {lesson.time}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-7150">
                              {lesson.format === 'Virtual' ? <VideoIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7151" /> : <MapPinIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7152" />}
                              <span className={isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-7153">
                                {lesson.format === 'Virtual' ? 'Online' : lesson.format}
                              </span>
                            </div>
                          </div>
                          {lesson.notes && <p className={`text-xs mt-1.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7154">
                              🎵 {lesson.notes}
                            </p>}
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 flex-shrink-0" data-id="element-7155">
                        {lesson.format === 'Virtual' && lesson.rawStatus !== 'COMPLETED' && lesson.rawStatus !== 'CANCELED' && isLessonOnline(lesson.location) && (() => {
                    const now = new Date();
                    const lessonStart = new Date(lesson.startAt);
                    const lessonEnd = new Date(lessonStart.getTime() + lesson.durationMinutes * 60 * 1000);
                    const windowStart = new Date(lessonStart.getTime() - 60 * 60 * 1000);
                    const windowEnd = new Date(lessonEnd.getTime() + 15 * 60 * 1000);
                    const inWindow = now >= windowStart && now <= windowEnd;
                    return inWindow;
                  })() && <button onClick={e => {
                    e.stopPropagation();
                    setSelectedLessonForJoin(lesson);
                    setIsJoinLessonPanelOpen(true);
                  }} className="px-4 py-2 bg-transparent border-2 border-green-500 text-green-500 hover:bg-green-500/10 rounded-lg text-sm font-medium whitespace-nowrap transition-colors" data-id="element-7156">
                              Join Now
                            </button>}
                      </div>
                    </div>

                    {/* Task section — outside flex row for proper button centering */}
                    {lesson.taskTitle && <div className={`mt-3 mx-5 p-3 rounded-lg border ${isDark ? 'bg-navy-900/50 border-navy-700' : 'bg-gray-50/80 border-gray-100'}`} data-id="element-7157">
                        <div className="flex items-center gap-1.5" data-id="element-7158">
                          <BookOpenIcon className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} data-id="element-7159" />
                          <span className={`text-xs font-semibold ${isDark ? 'text-blue-300' : 'text-blue-600'}`} data-id="element-7160">
                            Task:
                          </span>
                          <span className={`text-xs font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-7161">
                            {lesson.taskTitle}
                          </span>
                          {lesson.taskStatus && <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 capitalize ${lesson.taskStatus.toLowerCase() === 'completed' ? isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700' : lesson.taskStatus.toLowerCase() === 'in progress' || lesson.taskStatus.toLowerCase() === 'in-progress' ? isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700' : isDark ? 'bg-gray-600/40 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-7162">
                              {lesson.taskStatus}
                            </span>}
                        </div>
                        {(lesson.taskDueDate || lesson.taskNotes) && <div className="flex flex-col gap-1 mt-1.5 ml-5" data-id="element-7163">
                            {lesson.taskDueDate && <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7164">
                                <span className="font-medium" data-id="element-7165">Due:</span>{' '}
                                {lesson.taskDueDate}
                              </span>}
                            {lesson.taskNotes && <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-7166">
                                <span className={`font-medium ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7167">
                                  Notes:
                                </span>{' '}
                                {lesson.taskNotes}
                              </p>}
                          </div>}
                      </div>}
                  </div>;
          })()}

              {/* View My Schedule CTA */}
              <button onClick={() => navigate('/student-schedule')} className={`flex items-center gap-2 text-sm font-medium transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`} data-id="element-7168">
                View My Schedule
                <ChevronRightIcon className="h-4 w-4" data-id="element-7169" />
              </button>
            </div> :
        // Empty state
        <div className={`rounded-xl p-12 text-center ${isDark ? 'bg-navy-800/50 border-2 border-dashed border-navy-700' : 'bg-white border-2 border-dashed border-gray-200 shadow-sm'}`} data-id="element-7170">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-navy-700' : 'bg-gray-100'}`} data-id="element-7171">
                <CalendarIcon className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-7172" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-7173">
                No upcoming lessons yet
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7174">
                Your upcoming lessons will appear here.
              </p>
              <button onClick={() => navigate('/find-teachers')} className="px-6 py-2.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg font-medium transition-colors" data-id="element-7175">
                Find a Teacher
              </button>
            </div>}
        </div>
      </div>

      {/* Student Join Lesson Panel */}
      <StudentJoinLessonPanel isOpen={isJoinLessonPanelOpen} onClose={() => {
      setIsJoinLessonPanelOpen(false);
      setSelectedLessonForJoin(null);
    }} lesson={selectedLessonForJoin ? {
      id: selectedLessonForJoin.id,
      teacherName: selectedLessonForJoin.teacherName,
      meetingLink: selectedLessonForJoin.meetingLink
    } : null} darkMode={isDark} data-id="element-7176" />
    </div>;
};